import React from "react";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { AssigneeSelector } from "@/components/candidates/AssigneeSelector";
import { SkillsEditor } from "@/components/candidates/SkillsEditor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { CompanySelector } from "@/components/jobs/CompanySelector";
import { ContactSelector } from "@/components/jobs/ContactSelector";
import { useCandidateStatus } from "@/utils/candidate/statusUtils";

export interface EditField {
  name: string;
  type: 'text' | 'email' | 'tel' | 'url' | 'assignee' | 'skills' | 'textarea' | 
        'checkbox-group' | 'radio-group' | 'multi-select' | 'date' | 'select' | 'company-selector' | 'contact-selector';
  label: string;
  placeholder?: string;
  required?: boolean;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options?: string[];
  allowCustom?: boolean;
  customField?: string;
  conditionalFields?: Record<string, string[]>;
  showCondition?: (entity: any) => boolean;
}

export interface EntityEditFormProps {
  fields: EditField[];
  title?: string;
  className?: string;
  showSkillsAtBottom?: boolean;
  gridCols?: 1 | 2;
  isEditing?: boolean;
  entity?: any; // For contact-selector to access companyId
}

export const EntityEditForm: React.FC<EntityEditFormProps> = ({
  fields,
  title,
  className = "",
  showSkillsAtBottom = false,
  gridCols = 2,
  isEditing = true,
  entity
}) => {
  const { t } = useTranslation();
  const { getLocalizedStatus } = useCandidateStatus();

  const getSelectOptionLabel = (field: EditField, value: string) => {
    if (field.name === 'educationLevel')
      return t(`jobs.formWizard.educationLevels.${educationLevelKeyMap[value] || value}`);

    if (field.customField === 'candidate-status')
      return getLocalizedStatus(value);

    if (field.customField === 'job-status')
      return t(`jobs.formWizard.statuses.${value}`, value);

    if (field.customField === 'company-relationship')
      return t(`companies.relationship.${value}`, value);

    return value;
  };

  // Mapping for educationLevel keys to translation keys
  const educationLevelKeyMap: Record<string, string> = {
    HIGH_SCHOOL: "highSchool",
    ASSOCIATE: "associate",
    BACHELOR: "bachelor",
    MASTER: "master",
    DOCTORATE: "doctorate",
    NOT_REQUIRED: "notRequired"
  };

  // Separate skills field from other fields
  const skillsField = fields.find(field => field.type === 'skills');
  const regularFields = fields.filter(field => field.type !== 'skills');
  
  // For skills tab, include skills fields in regular fields
  const fieldsToRender = showSkillsAtBottom ? regularFields : fields;

  const renderField = (field: EditField) => {
    const commonProps = {
      value: field.value as string,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => 
        field.onChange(e.target.value),
      placeholder: field.placeholder,
      required: field.required,
      className: "bg-gray-50 border-0 focus:ring-0"
    };

    switch (field.type) {
      case 'email':
        return <Input type="email" {...commonProps} disabled={!isEditing} />;
      case 'tel':
        return <Input type="tel" {...commonProps} disabled={!isEditing} />;
      case 'url':
        return <Input type="url" {...commonProps} disabled={!isEditing} />;
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            className="bg-gray-50 border-0 focus:ring-0 p-3 min-h-[80px] resize-none"
            onChange={(e) => field.onChange(e.target.value)}
            disabled={!isEditing}
          />
        );
      case 'assignee':
        return (
          <AssigneeSelector
            value={field.value as string}
            onValueChange={(value) => field.onChange(value)}
            placeholder={field.placeholder}
            disabled={!isEditing}
          />
        );
      case 'select':
        return (
          <Select
            value={field.value as string}
            onValueChange={(value) => field.onChange(value)}
            disabled={!isEditing}
          >
            <SelectTrigger className="bg-gray-50 border-0 focus:ring-0">
              <SelectValue placeholder={field.placeholder}>
                {field.value ? getSelectOptionLabel(field, field.value as string) : field.value}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(option => (
                <SelectItem key={option} value={option}>
                  {getSelectOptionLabel(field, option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'company-selector':
        return (
          <CompanySelector
            selectedCompanyId={field.value as string}
            onSelectCompany={(id, name) => {
              // Pass both id and name as a special format that JobProfile can handle
              field.onChange(JSON.stringify({ id, name }));
            }}
            required={field.required}
          />
        );
      case 'contact-selector':
        return (
          <ContactSelector
            companyId={entity.company?.id || entity.companyId || ''}
            onSelectContact={(contactId) => field.onChange(contactId)}
            selectedContactId={field.value as string}
            disabled={!entity.company?.id && !entity.companyId}
          />
        );
      case 'checkbox-group':
        const selectedValues = Array.isArray(field.value) ? field.value : 
          (field.value ? field.value.split(',').map(v => v.trim()) : []);
        
        return (
          <div className="space-y-2">
            {field.options?.map(option => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.name}-${option}`}
                  checked={selectedValues.includes(option)}
                  onCheckedChange={(checked) => {
                    const newValues = checked
                      ? [...selectedValues, option]
                      : selectedValues.filter(v => v !== option);
                    field.onChange(newValues.join(', '));
                  }}
                  disabled={!isEditing}
                />
                <Label htmlFor={`${field.name}-${option}`} className="text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        );
      case 'radio-group':
        return (
          <RadioGroup
            value={field.value as string}
            onValueChange={(value) => field.onChange(value)}
            disabled={!isEditing}
          >
            {field.options?.map(option => (
              <div key={option} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`${field.name}-${option}`} />
                <Label htmlFor={`${field.name}-${option}`} className="text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );
      case 'multi-select':
        const multiSelectedValues = Array.isArray(field.value) ? field.value : 
          (field.value ? field.value.split(',').map(v => v.trim()) : []);
        
        return (
          <div className="space-y-2">
            {field.options?.map(option => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.name}-${option}`}
                  checked={multiSelectedValues.includes(option)}
                  onCheckedChange={(checked) => {
                    const newValues = checked
                      ? [...multiSelectedValues, option]
                      : multiSelectedValues.filter(v => v !== option);
                    field.onChange(newValues.join(', '));
                  }}
                  disabled={!isEditing}
                />
                <Label htmlFor={`${field.name}-${option}`} className="text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        );
      case 'date':
        return (
          <Input 
            type="date" 
            {...commonProps} 
            disabled={!isEditing}
            value={field.value as string || ''}
          />
        );
      case 'skills':
        // Convert string to array if needed
        const skillsArray = Array.isArray(field.value) ? field.value : 
          (field.value ? field.value.split(',').map(v => v.trim()).filter(Boolean) : []);
        
        return (
          <SkillsEditor
            skills={skillsArray}
            onChange={(skills) => field.onChange(skills.join(', '))}
            label=""
            placeholder={field.placeholder}
            addButtonText={t('candidates.newCandidateModal.addSkillButton')}
          />
        );
      case 'text':
      default:
        return <Input type="text" {...commonProps} disabled={!isEditing} />;
    }
  };

  const gridClass = gridCols === 2 ? "grid md:grid-cols-2 gap-4" : "grid gap-4";

  return (
    <div className={className}>
      {title && (
        <h3 className="text-lg font-semibold mb-3">{title}</h3>
      )}
      
      <div className={gridClass}>
        {fieldsToRender.filter(field => !field.showCondition || field.showCondition(entity)).map((field) => (
          <div key={field.name} className="flex flex-col">
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {field.label}
            </label>
            {renderField(field)}
          </div>
        ))}
      </div>

      {/* Skills section - full width at bottom */}
      {showSkillsAtBottom && skillsField && (
        <div className="mt-6">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {skillsField.label}
            </label>
            <SkillsEditor
              skills={skillsField.value as string[]}
              onChange={(skills) => skillsField.onChange(skills)}
              placeholder={skillsField.placeholder}
              addButtonText={t('candidates.newCandidateModal.addSkillButton')}
            />
          </div>
        </div>
      )}
    </div>
  );
}; 